/**
 * onboardingProgress.js
 * CODiii — onboarding milestone progress.
 */

const STORAGE_PREFIX = 'codiii_onboarding_progress';
const API_BASE = process.env.REACT_APP_ONBOARDING_API_BASE;

let currentUserId = null;

export function setOnboardingUserId(userId) {
  currentUserId = userId || null;
}

function resolveUserId(explicitUserId) {
  return explicitUserId || currentUserId || window.posthog?.get_distinct_id?.() || 'anon';
}
function storageKey(explicitUserId) {
  return `${STORAGE_PREFIX}:${resolveUserId(explicitUserId)}`;
}

// ── local cache  ──
function readState(userId) {
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return { events: [], completedAt: null };
    const parsed = JSON.parse(raw);
    return {
      events: Array.isArray(parsed.events) ? parsed.events : [],
      completedAt: parsed.completedAt ?? null,
    };
  } catch (_) {
    return { events: [], completedAt: null };
  }
}
function writeState(userId, state) {
  try { localStorage.setItem(storageKey(userId), JSON.stringify(state)); } catch (_) {}
}

// ── auth'd fetch ──
function authFetch(path, options = {}) {
  const idToken = localStorage.getItem('id_token');
  return fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ── SYNC cache readers (fallback + live in-session updates) ──
export function getCompletedMilestones(userId) {
  return new Set(readState(userId).events);
}
export function getCompletedAt(userId) {
  const ts = readState(userId).completedAt;
  return ts ? new Date(ts) : null;
}

// ── SERVER source of truth: fetch, reconcile with cache, persist, return ──
export async function fetchOnboardingProgress(userId) {
  const local = readState(userId);
  try {
    const res = await authFetch('/progress', { method: 'GET' });
    if (!res.ok) throw new Error(`progress GET ${res.status}`);
    const data = await res.json(); // { completedEvents: string[], completedAt: string|null }

    const serverEvents = Array.isArray(data.completedEvents) ? data.completedEvents : [];
    const merged = Array.from(new Set([...serverEvents, ...local.events]));
    const completedAt = data.completedAt ?? local.completedAt ?? null;

    writeState(userId, { events: merged, completedAt });

    // Heal dropped writes: push anything the server is missing (offline marks).
    merged.filter((e) => !serverEvents.includes(e)).forEach((e) => { void postMilestone(e); });
    if (completedAt && !data.completedAt) void postCompletedAt(completedAt);

    return { events: new Set(merged), completedAt: completedAt ? new Date(completedAt) : null };
  } catch (_) {
    // Offline / API down — fall back to cache so the UI still works.
    return {
      events: new Set(local.events),
      completedAt: local.completedAt ? new Date(local.completedAt) : null,
    };
  }
}

function postMilestone(event) {
  return authFetch('/progress/complete', {
    method: 'POST', body: JSON.stringify({ event }),
  }).catch(() => {});
}
function postCompletedAt(iso) {
  return authFetch('/progress/completed-at', {
    method: 'POST', body: JSON.stringify({ completedAt: iso }),
  }).catch(() => {});
}

export function markMilestoneComplete(event, userId) {
  if (!event) return;
  const state = readState(userId);
  if (!state.events.includes(event)) {
    state.events.push(event);
    writeState(userId, state);
  }
  void postMilestone(event); // idempotent server-side 
}

export function setCompletedAt(userId, date = new Date()) {
  const state = readState(userId);
  if (!state.completedAt) {
    const iso = date.toISOString();
    state.completedAt = iso;
    writeState(userId, state);
    void postCompletedAt(iso); // server stamps once (conditional write)
  }
}

export function resetOnboardingProgress(userId) {
  try { localStorage.removeItem(storageKey(userId)); } catch (_) {}
  void authFetch('/progress', { method: 'DELETE' }).catch(() => {});
}